import { useEffect, useRef, useState } from 'react'

import type { PdfDocumentLike } from '@/features/reader/hooks/usePdfDocument'
import { isPageReleased } from '@/features/reader/utils/toc'
import type { BookAccess } from '@/types/api'

export async function findPages(doc: PdfDocumentLike, access: BookAccess, query: string,
  cancelled: () => boolean = () => false) {
  const needle = query.trim().toLocaleLowerCase('pt-BR')
  const results: { page: number; snippet: string }[] = []
  if (!needle) return results
  for (let page = 1; page <= doc.numPages; page++) {
    if (cancelled()) return []
    if (!isPageReleased(access, page)) continue
    const pdfPage = await doc.getPage(page)
    if (cancelled()) return []
    const content = await pdfPage.getTextContent()
    if (cancelled()) return []
    const text = content.items.map(item => item.str ?? '').join(' ').replace(/\s+/g, ' ')
    const position = text.toLocaleLowerCase('pt-BR').indexOf(needle)
    if (position >= 0) results.push({ page, snippet: text.slice(Math.max(0, position - 45), position + needle.length + 80) })
    // Uma ocorrência por página, com limite explícito na interface.
    if (results.length === 100) break
  }
  return results
}

export function PdfSearch({ doc, access, onGo }: {
  doc: PdfDocumentLike; access: BookAccess; onGo: (page: number) => void
}) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<Awaited<ReturnType<typeof findPages>>>([])
  const [status, setStatus] = useState('')
  const generation = useRef(0)
  useEffect(() => () => { generation.current++ }, [doc])
  async function search() {
      const run = ++generation.current
      setResults([]); setStatus('Buscando nas páginas liberadas…')
      try {
        const found = await findPages(doc, access, query, () => run !== generation.current)
        if (run !== generation.current) return
        setResults(found)
        setStatus(found.length === 100 ? 'Primeiras 100 páginas encontradas. Refine a busca.'
          : `${found.length} página(s) encontrada(s) nas páginas liberadas.`)
      } catch {
        if (run === generation.current) setStatus('Não foi possível concluir a busca. Tente novamente.')
      }
  }
  return <details className="reader-study-tool">
    <summary>Buscar no livro</summary>
    <form onSubmit={event => { event.preventDefault(); void search() }}>
      <label htmlFor="pdf-search">Buscar texto no PDF</label>
      <input id="pdf-search" type="search" value={query} maxLength={200} required onChange={event => {
        generation.current++; setQuery(event.target.value); setResults([]); setStatus('')
      }} />
      <button type="submit" disabled={!query.trim()}>Buscar</button>
      <button type="button" onClick={() => { generation.current++; setStatus('Busca cancelada.'); setResults([]) }}>Cancelar busca</button>
    </form>
    <p role="status">{status}</p>
    <ol>{results.map(result => <li key={result.page}>
      <button type="button" onClick={() => onGo(result.page)}>Página {result.page}: {result.snippet}</button>
    </li>)}</ol>
  </details>
}
